// LifeInMotion.jsx
import React from "react";
import { Parallax } from "react-scroll-parallax";

const LifeInMotion = () => {
  return (
    <section className="relative overflow-hidden bg-black px-6 py-24 text-white md:px-20">
      <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-white opacity-5 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-white opacity-5 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-3xl text-center">
        <h2 className="mb-6 animate-fadeIn text-3xl font-bold md:text-4xl">
          Life in Motion
        </h2>
        <p className="mb-12 animate-fadeIn text-white delay-200 md:text-lg">
          Running and cycling teach endurance, strategy, and resilience, lessons
          that mirror my professional journey.
        </p>
      </div>

      <div className="relative z-10 md:flex md:gap-8">
        <Parallax speed={15}>
          <div className="relative mb-6 overflow-hidden rounded-xl shadow-2xl transition-all hover:scale-105 md:mb-0">
            <img
              src="/images/ChinaRun.jpeg"
              alt="Kenneth Milanzi Running"
              className="h-full w-full object-contain"
            />
            <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-t from-black/40 to-transparent" />
          </div>
        </Parallax>

        <Parallax speed={-15}>
          <div className="relative overflow-hidden rounded-xl shadow-2xl transition-all hover:scale-105">
            <img
              src="/images/BeKind.jpeg"
              alt="Kenneth Milanzi Cycling"
              className="h-full w-full object-contain"
            />
            <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-t from-black/40 to-transparent" />
          </div>
        </Parallax>
      </div>

      <div className="relative z-10 mx-auto mt-16 max-w-5xl">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 shadow-2xl backdrop-blur-sm md:p-10">
          <h3 className="mb-6 text-2xl font-bold md:text-3xl">
            More Than Movement
          </h3>

          <div className="space-y-5 text-base leading-8 text-white/85 md:text-lg">
            <p>
              Running and cycling have become more than physical disciplines.
              They are practices that have shaped how I think, lead, and stay
              grounded through complexity. Long distances teach patience. Hills
              teach persistence. Every route reinforces the value of preparation,
              rhythm, and staying committed even when progress feels gradual.
            </p>

            <p>
              In many ways, the road mirrors professional life. Not every phase
              is about speed. Some moments demand consistency, thoughtful pacing,
              and the discipline to keep moving with purpose. Whether navigating
              sustainability strategy, stakeholder engagement, or long-term
              transformation, the same principles apply: stay focused on the
              bigger objective, adapt to changing conditions, and keep enough
              perspective to know when to push and when to conserve energy.
            </p>

            <p>
              Cycling has especially taught me the importance of strategy.
              Terrain changes quickly, and success depends on reading the path
              ahead while remaining responsive in the present. Running has taught
              me resilience, the kind built quietly over time through repetition,
              discomfort, and trust in the process. Together, they have
              strengthened my appreciation for endurance, clarity, and momentum.
            </p>

            <p>
              These experiences continue to influence how I approach leadership
              and growth. I value preparation, steady execution, and the ability
              to remain composed through uncertainty. Movement reminds me that
              progress is rarely linear, but with intention, adaptability, and
              discipline, meaningful outcomes are always within reach.
            </p>
          </div>
        </div>
      </div>

      <div className="relative z-10 mt-12 md:flex md:gap-8">
        <Parallax speed={12}>
          <div className="relative mb-6 overflow-hidden rounded-xl shadow-2xl transition-all hover:scale-105 md:mb-0">
            <img
              src="/images/Spartan.jpeg"
              alt="Kenneth Milanzi Spartan challenge"
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-t from-black/40 to-transparent" />
          </div>
        </Parallax>

        <Parallax speed={-12}>
          <div className="relative overflow-hidden rounded-xl shadow-2xl transition-all hover:scale-105">
            <img
              src="/images/KenzoCycle.jpg"
              alt="Kenneth Milanzi With Absa Cycling team"
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-t from-black/40 to-transparent" />
          </div>
        </Parallax>
      </div>
    </section>
  );
};

export default LifeInMotion;